import { streamChat, StreamUpdate } from './agentApi';
import { ChatRequest } from './types';
import { useAgentChatStore } from '@shared/stores';

// Logger utility - safe check for development mode
const isDevelopment = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development';

const logger = {
    log: (...args: any[]) => {
        if (isDevelopment) {
            console.log(...args);
        }
    },
    error: (...args: any[]) => {
        console.error(...args);
    }
};

/**
 * Agent Stream Manager
 * Manages streaming at a global level to prevent loss of responses during navigation
 * 
 * This service ensures that:
 * 1. Streams continue even if component unmounts
 * 2. Store receives all updates regardless of component lifecycle
 * 3. Only one stream can be active at a time
 */

class AgentStreamManager {
  private activeController: AbortController | null = null;
  private isStreamActive: boolean = false;

  /**
   * Start a new chat stream
   * Automatically cancels any existing stream before starting
   */
  async startStream(
    payload: ChatRequest,
    assistantMessageId: string,
    token?: string
  ): Promise<void> {
    // Cancel existing stream if any
    this.cancelStream();

    // Create new AbortController for this stream
    this.activeController = new AbortController();
    this.isStreamActive = true;

    // Get store reference
    const store = useAgentChatStore.getState();
    
    // Mark stream as active in store
    store.setActiveStream(this.activeController, assistantMessageId);
    store.setIsLoading(true);

    logger.log(`🚀 AgentStreamManager: Starting stream for message ${assistantMessageId}`);

    try {
      await streamChat(
        payload,
        (update: StreamUpdate) => {
          // Update store directly (bypasses component lifecycle)
          const currentStore = useAgentChatStore.getState();
          
          switch (update.type) {
            case 'thinking':
              currentStore.setThinkingStep(update.content);
              break;
            
            case 'replace':
              currentStore.updateMessage(assistantMessageId, update.content);
              break;
            
            case 'append':
              currentStore.appendToMessage(assistantMessageId, update.content);
              break;
          }
        },
        (error) => {
          logger.error("❌ AgentStreamManager: Stream error", error);
          this.handleStreamComplete();
        },
        () => {
          logger.log("✅ AgentStreamManager: Stream completed successfully");
          this.handleStreamComplete();
        },
        token
      );
    } catch (error) {
      logger.error("❌ AgentStreamManager: Failed to start stream", error);
      this.handleStreamComplete();
      throw error;
    }
  }

  /**
   * Cancel active stream
   */
  cancelStream(): void {
    if (this.activeController && this.isStreamActive) {
      logger.log("⛔ AgentStreamManager: Cancelling active stream");
      this.activeController.abort();
      this.handleStreamComplete();
    }
  }

  /**
   * Check if stream is currently active
   */
  isActive(): boolean {
    return this.isStreamActive;
  }

  /**
   * Get active message ID
   */
  getActiveMessageId(): string | null {
    return useAgentChatStore.getState().pendingMessageId;
  }

  /**
   * Handle stream completion (success or error)
   */
  private handleStreamComplete(): void {
    this.isStreamActive = false;
    this.activeController = null;

    const store = useAgentChatStore.getState();
    store.setIsLoading(false);
    store.setThinkingStep(null);
    store.setActiveStream(null, null);
  }
}

// Export singleton instance
export const agentStreamManager = new AgentStreamManager();
